import React from 'react';
import { useDispatch } from 'react-redux';
import { WATERMARK_TYPE, WATERMARK_POSITION, WatermarkType, serviceSettingsActions } from '../../../reducers/serviceSettings';
import { watermarkRadioButtons } from '../constants';
import ToggleSwitch from '../ui/ToggleSwitch';
import SliderWithNumberInput from '../ui/SliderWithNumberInput';
import ColorAndPositionRow from '../ui/ColorAndPositionRow';

interface WatermarkSettingsSectionProps {
  serviceSettings: any;
  onSettingsChanged: () => void;
}

const WatermarkSettingsSection: React.FC<WatermarkSettingsSectionProps> = ({
  serviceSettings,
  onSettingsChanged,
}) => {
  const dispatch = useDispatch();

  const handleTypeChange = (type: WatermarkType) => {
    dispatch(serviceSettingsActions.setWatermarkType(type));
    if (type === WATERMARK_TYPE.CORPORATE_LOGO && serviceSettings.corporate_logo_file_data) {
      dispatch(serviceSettingsActions.setCorporateLogoIsVisible(true));
    }
    onSettingsChanged();
  };

  const isEnabled = serviceSettings.watermark_type !== WATERMARK_TYPE.NONE;
  const isIndividualInput = serviceSettings.watermark_type === WATERMARK_TYPE.INDIVIDUAL_INPUT;
  const isCorporateLogo = serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO;

  const transparencyValue = isCorporateLogo
    ? (serviceSettings.corporate_logo_transparency ?? 100)
    : (serviceSettings.individual_input_transparency ?? 100);

  const scaleValue = isCorporateLogo
    ? (serviceSettings.corporate_logo_scale ?? 100)
    : (serviceSettings.individual_input_scale ?? 100);

  const handleTransparencyChange = (value: number) => {
    if (isCorporateLogo) {
      dispatch(serviceSettingsActions.setCorporateLogoTransparency(value));
    } else {
      dispatch(serviceSettingsActions.setIndividualInputTransparency(value));
    }
    onSettingsChanged();
  };

  const handleScaleChange = (value: number) => {
    if (isCorporateLogo) {
      dispatch(serviceSettingsActions.setCorporateLogoScale(value));
    } else {
      dispatch(serviceSettingsActions.setIndividualInputScale(value));
    }
    onSettingsChanged();
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 500, fontSize: 15, color: '#444' }}>ウォーターマーク</div>
        <ToggleSwitch
          checked={isEnabled}
          onToggle={() => {
            handleTypeChange(isEnabled ? WATERMARK_TYPE.NONE : WATERMARK_TYPE.INDIVIDUAL_INPUT);
          }}
        />
      </div>

      {isEnabled && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: '#f5f5f5',
          borderRadius: 8,
          padding: 16,
        }}>
          {/* ラジオボタン（個別入力 / 企業ロゴ） */}
          <div style={{ display: 'flex', gap: 16, padding: '8px 0' }}>
            {watermarkRadioButtons.filter(w => w.type !== WATERMARK_TYPE.NONE).map(wt => (
              <label
                key={wt.type}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: '#444',
                }}
              >
                <input
                  type="radio"
                  name="watermark-type"
                  checked={serviceSettings.watermark_type === wt.type}
                  onChange={() => handleTypeChange(wt.type)}
                  style={{ cursor: 'pointer', width: 16, height: 16 }}
                />
                <span style={{ fontSize: 15 }}>{wt.label}</span>
              </label>
            ))}
          </div>

          {/* 個別入力 設定領域 */}
          {isIndividualInput && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#444', fontSize: 14, fontWeight: 500 }}>
                  テキスト
                </label>
                <input
                  type="text"
                  value={serviceSettings.individual_input_text || ''}
                  onChange={e => {
                    dispatch(serviceSettingsActions.setIndividualInputText(e.target.value.slice(0, 30)));
                    onSettingsChanged();
                  }}
                  style={{
                    fontSize: 14,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #bbb',
                    width: '100%',
                    lineHeight: '20px',
                  }}
                  maxLength={30}
                  placeholder="テキストを入力（最大30文字）"
                />
              </div>
              <ColorAndPositionRow
                colorValue={serviceSettings.individual_input_color}
                onColorChange={(color) => {
                  dispatch(serviceSettingsActions.setIndividualInputColor(color));
                  onSettingsChanged();
                }}
                positionValue={serviceSettings.watermark_position || 'center'}
                onPositionChange={(position) => {
                  dispatch(serviceSettingsActions.setWatermarkPosition(position));
                  onSettingsChanged();
                }}
              />
            </>
          )}

          {/* 透明度・サイズ */}
          <div style={{ display: 'flex', gap: 16 }}>
            <SliderWithNumberInput
              label="透明度："
              value={transparencyValue}
              onChange={handleTransparencyChange}
            />
            <SliderWithNumberInput
              label="サイズ："
              value={scaleValue}
              onChange={handleScaleChange}
            />
          </div>

          {/* 配置位置選択（企業ロゴ時のみ） */}
          {isCorporateLogo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ color: '#444', fontSize: 14, whiteSpace: 'nowrap' }}>配置：</label>
              <select
                value={serviceSettings.watermark_position || 'center'}
                onChange={(e) => {
                  dispatch(serviceSettingsActions.setWatermarkPosition(e.target.value));
                  onSettingsChanged();
                }}
                style={{
                  fontSize: 14,
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #bbb',
                  background: '#fafafa',
                }}
              >
                {WATERMARK_POSITION.map((pos) => (
                  <option key={pos.value} value={pos.value}>{pos.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WatermarkSettingsSection;
